import {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import Swal from 'sweetalert2'
import {
  TApiPricingResponse,
  TApiPricingRequest,
} from '../../../coreGlobal/models/franchise/franchise'
import {formatMask, formatMonetaryToBr} from '../../../utils/formatMonetaryToBr'
import {useFormik} from 'formik'
import {franchiseApiPricingSchema} from '../core/validation/FanchiseVali'
import {buildUpdateApiPricingRequest} from '../core/buildUpdateApiPricingRequest'
import {updateApiPricing} from '../core/_request'
import {PageOverlayer} from '../../../components/PageOverlayer'

type TServiceAmountProps = {
  apiPricing: TApiPricingResponse
}

export const ServiceAmountForm = ({apiPricing}: TServiceAmountProps) => {
  const [loading, setLoading] = useState(false)
  const [loadingPage, setLoadingPage] = useState(false)

  let {id} = useParams()

  const formik = useFormik<TApiPricingResponse>({
    initialValues: [
      {
        id: 0,
        franchiseId: 0,
        endpoint: '',
        notes: '',
        pricePerRequest: '',
        validFrom: '',
      },
    ],
    validationSchema: franchiseApiPricingSchema,
    onSubmit: async (values) => {
      const valuesFilter = await buildUpdateApiPricingRequest(values)
      submit(valuesFilter)
    },
  })

  useEffect(() => {
    if (apiPricing.length === 0) return

    setLoadingPage(true)

    const values = apiPricing?.map((field) => ({
      id: field?.id,
      franchiseId: field?.franchiseId,
      endpoint: field?.endpoint,
      notes: field?.notes,
      pricePerRequest: formatMonetaryToBr(field?.pricePerRequest),
      validFrom: field?.validFrom,
    }))

    formik.setValues(values)

    setLoadingPage(false)
  }, [apiPricing])

  const handleOnChangeAmount =
    (index: number, variable: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue(`[${index}].${variable}`, formatMask(event.target.value))
    }

  const submit = async (valuesFilter: TApiPricingRequest) => {
    Swal.fire({
      title: 'Deseja alterar os valores das despesas?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger', // Classe personalizada para o botão de confirmação
        cancelButton: 'btn btn-secondary', // Classe personalizada para o botão de cancelamento (opcional)
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true)
        try {
          await updateApiPricing(String(id), valuesFilter)
          await Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Salvo com sucesso',
            showConfirmButton: false,
            timer: 1500,
          })
        } catch (error: any) {
          await Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: error.response?.data?.message,
          })
        } finally {
          setLoading(false)
        }
      }
    })
  }

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <div className='card card-custom mb-5 mb-xl-10'>
          <div className='card-header'>
            <h3 className='card-title fw-bolder m-0'>Valor Despesas</h3>
            <div className='card-toolbar'></div>
          </div>
          {loadingPage ? (
            <PageOverlayer />
          ) : (
            <>
              <div className='card-body d-flex overflow-auto'>
                <table className='table table-row-dashed table-row-gray-300 align-middle'>
                  <thead>
                    <tr className='fw-bolder fs-6 text-gray-800'>
                      <th style={{minWidth: '50px'}}>Número</th>
                      <th style={{minWidth: '150px'}}>Nome</th>
                      <th style={{minWidth: '150px'}}>Preço por solicitação</th>
                      <th style={{minWidth: '150px'}}>Valido apartir</th>
                      <th style={{minWidth: '250px'}}>Obs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(formik.values) &&
                      formik.values.map((field, index) => {
                        return (
                          <tr key={`serviceAmount${index}`}>
                            <td className='text-center'>{index + 1}</td>

                            <td>
                              <div className='column col-lg-12'>
                                <input
                                  type='text'
                                  maxLength={255}
                                  className='form-control form-control-lg form-control-solid'
                                  placeholder='Nome'
                                  {...formik.getFieldProps(`${index}.endpoint`)}
                                  value={formik.values[index]?.endpoint ?? ''}
                                />

                                {formik.errors?.[index]?.endpoint && (
                                  <div className='text-danger'>
                                    {formik.errors[index]?.endpoint as string}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className='column col-lg-12'>
                                <input
                                  type='text'
                                  maxLength={14}
                                  className='form-control form-control-lg form-control-solid'
                                  placeholder='Preço por solicitação'
                                  {...formik.getFieldProps(`${index}.pricePerRequest`)}
                                  onChange={handleOnChangeAmount(index, 'pricePerRequest')}
                                  value={formik.values[index]?.pricePerRequest ?? ''}
                                />
                                {formik.errors?.[index]?.pricePerRequest && (
                                  <div className='text-danger'>
                                    {formik.errors[index]?.pricePerRequest as string}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className='column col-lg-12'>
                                <input
                                  type='date'
                                  maxLength={14}
                                  className='form-control form-control-lg form-control-solid'
                                  placeholder='Valido apartir'
                                  {...formik.getFieldProps(`${index}.validFrom`)}
                                  value={formik.values[index]?.validFrom ?? ''}
                                />
                                {formik.errors?.[index]?.validFrom && (
                                  <div className='text-danger'>
                                    {formik.errors[index]?.validFrom as string}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <textarea
                                maxLength={255}
                                className='form-control form-control-lg form-control-solid'
                                placeholder='Observação'
                                {...formik.getFieldProps(`${index}.notes`)}
                                value={formik.values[index]?.notes ?? ''}
                              />
                              {formik.errors?.[index]?.notes && (
                                <div className='text-danger'>
                                  {formik.errors[index]?.notes as string}
                                </div>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>
              <div className='card-footer d-flex justify-content-end py-6 px-9'>
                <button
                  type='submit'
                  className='btn btn-danger'
                  disabled={loading || !formik.isValid}
                >
                  {!loading && 'Salvar'}
                  {loading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      Salvando...{' '}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </form>
    </>
  )
}
